import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { queryKeys } from '../client/query-client';
import { ContentType } from '../content/contracts';
import { dayIn } from '../shared/dates';
/** Home's own reads (spec §4). Each row owns its query, so one slow call
 * never blocks the page — Home aggregates from seven sources. */
export const PhraseOfDay = z.object({
    day: z.string(),
    /** Null when no phrase is scheduled for today. Not an error. */
    phrase: z
        .object({
        id: z.string().uuid(),
        text: z.string(),
        attribution: z.string().nullable(),
    })
        .nullable(),
});
/**
 * `day` is REQUIRED and we were not sending it.
 *
 * The contract declares `day: string` (`YYYY-MM-DD`) as a required query
 * parameter; without it the API answers 400, the query errored on every load,
 * and the anchoring phrase rendered as an empty tinted bar rather than the
 * "row that simply does not appear" this screen intends.
 *
 * `dayIn()` is the platform timezone, not the device's — the same helper the
 * challenge log uses. A member in Vancouver at 22:00 local is on the same
 * Boussole day as everyone else, and a phrase that changes at the wrong
 * midnight is worse than one that changes at an inconvenient hour.
 *
 * The day is in the query key too: without it the cache would serve yesterday's
 * phrase across midnight until something else invalidated it.
 */
export function usePhraseOfDay(http, locale) {
    const day = dayIn();
    return useQuery({
        queryKey: [...queryKeys.phraseOfDay, day, locale],
        queryFn: () => http.get('/phrase-rotations/day', { schema: PhraseOfDay, locale, query: { day } }),
    });
}
export const Recommendation = z.object({
    id: z.string().uuid(),
    type: ContentType,
    /** THEIR segment. Our route is looked up from it, never built. */
    path: z.string(),
    title: z.string(),
    summary: z.string().optional(),
    premium: z.boolean(),
    /**
     * Whether THIS reader is locked out of it — the row's own answer, and
     * genuinely both ways here: Home recommends premium rows to everybody, so a
     * member without a subscription sees them locked and a member who **bought**
     * one sees that one open. Derived from `premium` it could only ever be the
     * first of those. Optional while the field is not deployed everywhere.
     */
    locked: z.boolean().optional(),
    /** Null is *not sold separately* — what turns the lock into an offer. */
    priceCents: z.number().int().nonnegative().nullable().optional(),
    coverUrl: z.string().nullable(),
    /** True when an answer named this item outright rather than matching it. */
    pinned: z.boolean(),
    /**
     * WHY this card is in front of you — the mock's `.cc-why` line.
     *
     * Discriminated rather than a pre-built sentence, so the client renders it
     * in the reader's language instead of the one the server happened to pick.
     *
     * `AGE_BAND.primary` separates the band an item was WRITTEN for from one it
     * merely stretches to — they score differently, so flattening them would
     * label a rail by something it is not ordered on.
     *
     * `NEW` marks the padding tail: a short rail is topped up with recent
     * audience-scoped items behind the personalised ones, append-only, so a
     * matched card is never behind an unmatched one.
     */
    reason: z
        .discriminatedUnion('kind', [
        z.object({ kind: z.literal('PINNED') }),
        z.object({ kind: z.literal('DESTINATION'), destinationId: z.string().uuid() }),
        z.object({
            kind: z.literal('AGE_BAND'),
            ageBandId: z.string().uuid(),
            primary: z.boolean().optional(),
        }),
        z.object({ kind: z.literal('PILLAR'), pillarId: z.string().uuid() }),
        z.object({ kind: z.literal('NEW') }),
    ])
        .optional(),
});
export const RecommendationRail = z.object({
    /** Empty when somebody has not answered — not an error, just nothing to go on. */
    items: z.array(Recommendation),
    destinationIds: z.array(z.string().uuid()),
    ageBandIds: z.array(z.string().uuid()),
});
export function useRecommendations(http, locale) {
    return useQuery({
        queryKey: ['onboarding', 'recommendations', locale],
        queryFn: () => http.get('/onboarding/recommendations', { schema: RecommendationRail, locale }),
    });
}
export const MemberAnnouncement = z.object({
    id: z.string().uuid(),
    key: z.string(),
    title: z.string(),
    body: z.string(),
    tickerUntil: z.string().datetime().nullable(),
    sentAt: z.string().datetime().nullable(),
    // The locale the item was AUTHORED in — used by the home ticker to mark
    // when it fell back (a French-only announcement served on the English
    // home). Distinct from the response-envelope `locale`, which is the one
    // the client REQUESTED.
    sourceLocale: z.string(),
});
/**
 * The ticker.
 *
 * Narrowed to `MemberAnnouncement` after the backend found that the shared
 * `Announcement` schema was serving `recipientCount` — the reach of a broadcast
 * — and the full `audiences` targeting set to every recipient of it. Neither is
 * here now, and their journey asserts the absence.
 */
export function useAnnouncementTicker(http, locale) {
    return useQuery({
        queryKey: ['announcements', 'ticker', locale],
        queryFn: () => http.get('/announcements/ticker', {
            schema: z.object({ items: z.array(MemberAnnouncement), locale: z.string() }),
            locale,
        }),
    });
}
//# sourceMappingURL=home.js.map